'use client';
import {
  CatBadge,
  CatButton,
  CatNumericInput,
  CatTextLink
} from 'components/blocks-components';
import { useTranslations } from 'next-intl';
import { Components } from 'services/fbtItems/types';
import { Image } from 'components/image';
import { CatIconShopFilled } from '@blocks-react/components/Icons/ShopFilled/ShopFilled';
import CommonAvailabilityWrapper from '../common-availability-wrapper';
import Price from 'components/price';
import styles from '../customize-kit-button/customize-kit-button.module.scss';
import {
  DealerPriceAvailabilityResponse,
  ProductDealerPriceAvailability
} from 'services/dealerPriceAvailability/types';
import { DealerResponse } from 'services/dealer';
import { LocationResponse } from 'services/location/types';
import { useParams } from 'next/navigation';
import { getSeoUrl } from 'utils/links';
import { SkeletonLoader } from '@cat-ecom/pcc-components';
import { CartResponse, OrderItemsResponseKit } from 'services/cart/types';
import { createHandleSelectItemEventGa } from './utils';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';

export interface KitMobileProps {
  priceAvailabilityDrawerData?: DealerPriceAvailabilityResponse;
  kitData?: Components[];
  valHandle?: (e: CustomEvent, i: number) => void;
  handleAddToCart?: (item?: Array<Components[]> | Components) => void;
  dealerInformation?: DealerResponse;
  locale?: string;
  availRemoveInterimStockFlag?: boolean;
  isShowSourcingDetailEnabled?: boolean;
  isDeliveryEnabled?: boolean;
  userStoreLocationData?: LocationResponse;
  isHeadlessPDPEnabled?: boolean;
  orderItems?: Array<OrderItemsResponseKit>;
  contactDealerComp?: React.ReactNode;
  dealerPriceData?: Record<string, ProductDealerPriceAvailability>;
  isLoadingInitial?: boolean;
  isLoadingRow?: Record<string, boolean>;
  mergedKitResponse;
  cartDetails?: CartResponse;
  storeId?: string;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
}
const KitMobile = ({
  kitData,
  valHandle,
  priceAvailabilityDrawerData,
  handleAddToCart,
  dealerInformation,
  locale,
  availRemoveInterimStockFlag,
  isShowSourcingDetailEnabled,
  isDeliveryEnabled,
  userStoreLocationData,
  isHeadlessPDPEnabled,
  orderItems,
  dealerPriceData,
  contactDealerComp,
  isLoadingInitial,
  isLoadingRow,
  mergedKitResponse,
  cartDetails,
  storeId,
  isRTL = false,
  currencyLdFeatureFlag = false
}: KitMobileProps) => {
  const t = useTranslations();
  const params = useParams();
  const esiteUrl = getSeoUrl(params);
  const combinedData = kitData?.map((item1) => {
    const item2 = priceAvailabilityDrawerData?.priceAndAvailability.find(
      (item) => item?.partNumber === item1?.partNumber
    );
    return { ...item1, price: item2 };
  });
  const getItemCount = (partNumber): string => {
    const itemCount = orderItems?.find((a) => a.partNumber === partNumber);
    return itemCount?.quantity?.toString() ?? '0';
  };
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const handleSelectItemEventGa = (clickedItem) => {
    createHandleSelectItemEventGa(
      clickedItem,
      mergedKitResponse,
      cartDetails,
      storeId,
      fireSelectItemEvent
    );
  };

  return (
    <>
      {combinedData?.map((item, index) => (
        <>
          <div className="d-flex">
            <div className={styles['kit-container-sm']}>
              <Image src={item.thumbnail} alt="img1" width="82" height="72" />
            </div>
            <div className={styles['part-number-sm']}>
              <CatTextLink
                href={
                  isHeadlessPDPEnabled
                    ? item?.partNumber
                    : `${esiteUrl}/${item?.partNumber}`
                }
                onClick={() => handleSelectItemEventGa(item)}
                className={styles['part-number-underline']}
              >
                {item?.partNumber}: {item?.name}
              </CatTextLink>
              <div className={styles['part-number-underline note-font']}>
                {item?.longDescription}
              </div>
            </div>
          </div>
          <div className={`${styles['inner-container-sm']} d-flex `}>
            <div className={styles['inner-container-width-sm']}>
              <label
                className={
                  !item?.price?.enableAddToCart
                    ? styles['quantity-sm-disabled']
                    : styles['quantity-sm']
                }
                htmlFor="quantity"
              >
                {t('SSRO_QUANTITY') + ':'}
              </label>
              <br />
              <CatNumericInput
                className={styles['quantity-picker-sm']}
                value={Number(item?.quantity)}
                onBlNumericInputChange={(e) => valHandle(e, index)}
                hideLabel
                size="sm"
                min={1}
                max={99999}
                data-testid="item-quantity-desktop"
                status={Number(item?.quantity) < 1 ? 'error' : null}
                disabled={!item?.price?.enableAddToCart || isLoadingRow[index]}
              >
                {Number(item.quantity) < 1 && (
                  <span slot="error">{t('INVALID_QUANTITY')}</span>
                )}
              </CatNumericInput>
              <div className={`${styles['inner-container-button-sm']} d-flex `}>
                {item?.price?.enableAddToCart ? (
                  <CatButton
                    disabled={Number(item?.quantity) < 1}
                    variant="primary"
                    onClick={() => handleAddToCart(item)}
                  >
                    {t('ADD_TO_CART')}
                  </CatButton>
                ) : (
                  <>{contactDealerComp}</>
                )}
                <div className={styles['shop-icon-sm']}>
                  <span className="m-0">
                    <CatBadge
                      position="top-right"
                      text={getItemCount(item?.partNumber)}
                      variant="brand"
                      data-testid="cat-header-cart-badge"
                      size="md"
                    />
                    <CatIconShopFilled size="lg" />
                  </span>
                </div>
              </div>
              {item?.price?.enableAddToCart &&
                (isLoadingInitial || isLoadingRow[index] ? (
                  <SkeletonLoader />
                ) : (
                  <CommonAvailabilityWrapper
                    availabilityData={
                      dealerPriceData?.[item?.partNumber] ?? item?.price
                    }
                    shouldDisplayLabel={false}
                    isLoading={false}
                    locale={locale}
                    dealerData={userStoreLocationData}
                    dealerInformation={dealerInformation}
                    availRemoveInterimStockFlag={availRemoveInterimStockFlag}
                    isShowSourcingDetailEnabled={isShowSourcingDetailEnabled}
                    isDeliveryEnabled={isDeliveryEnabled}
                  />
                ))}
            </div>
            {isLoadingInitial || isLoadingRow[index] ? (
              <SkeletonLoader className="w-50" />
            ) : (
              <div className={styles['kit-price-sm']}>
                <Price
                  price={dealerPriceData?.[item?.partNumber] ?? item?.price}
                  currency={priceAvailabilityDrawerData?.currency}
                  showDiscountSection={false}
                  isRTL={isRTL}
                  currencyLdFeatureFlag={currencyLdFeatureFlag}
                />
              </div>
            )}
          </div>
          <hr />
        </>
      ))}
    </>
  );
};
export default KitMobile;
